import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  TrendingUp, FileText, Calculator, Lightbulb, Activity, Users,
  ChevronDown, Printer, Download, DollarSign, Receipt, Clock,
  BarChart3, Briefcase
} from 'lucide-react';
import { reportsApi } from '../services/api';
import Loading from '../components/Loading';
import api from '../services/api';
import { useBusinessContext } from '../contexts/BusinessContext';
import UpgradePrompt from '../components/UpgradePrompt';

// Report Components
import HealthScorecard from '../components/reports/HealthScorecard';
import CustomerIntelligence from '../components/reports/CustomerIntelligence';
import ProfitLossReport from '../components/reports/ProfitLossReport';
import AnalyticsReport from '../components/reports/AnalyticsReport';
import ScheduleCReport from '../components/reports/ScheduleCReport';
import QuarterlyTaxReport from '../components/reports/QuarterlyTaxReport';
import PaymentSummary from '../components/reports/PaymentSummary';
import InvoiceAging from '../components/reports/InvoiceAging';
import JobsSummary from '../components/reports/JobsSummary';

// Report categories with their reports
const REPORT_CATEGORIES = {
  money: {
    id: 'money',
    label: 'Money',
    icon: DollarSign,
    color: 'green',
    reports: [
      { id: 'payments', label: 'Payments', icon: Receipt, tier: 'Solo', description: 'Track payments received' },
      { id: 'invoices', label: 'Invoice Aging', icon: Clock, tier: 'Solo', description: 'Outstanding invoices' },
      { id: 'profitLoss', label: 'Profit & Loss', icon: TrendingUp, tier: 'Professional', description: 'Revenue vs expenses' },
    ]
  },
  business: {
    id: 'business',
    label: 'Business',
    icon: BarChart3,
    color: 'blue',
    reports: [
      { id: 'scorecard', label: 'Action Plan', icon: Activity, tier: 'Solo', description: 'What to do next' },
      { id: 'jobs', label: 'Jobs', icon: Briefcase, tier: 'Solo', description: 'Job completion & revenue' },
      { id: 'customers', label: 'Customer Intel', icon: Users, tier: 'Professional', description: 'Customer insights' },
      { id: 'analytics', label: 'Insights', icon: Lightbulb, tier: 'Professional', description: 'Business analytics' },
    ]
  },
  tax: {
    id: 'tax',
    label: 'Tax Prep',
    icon: FileText,
    color: 'purple',
    reports: [
      { id: 'scheduleC', label: 'Schedule C', icon: FileText, tier: 'Solo', description: 'Annual tax prep' },
      { id: 'quarterly', label: 'Quarterly Tax', icon: Calculator, tier: 'Solo', description: 'Estimated payments' },
    ]
  }
};

const Reports = () => {
  const { canAccessFeature } = useBusinessContext();
  const location = useLocation();
  const canAccessPro = canAccessFeature('pnl-reports');
  
  // Check if this is a deep link on mount (use ref to persist across renders)
  const isDeepLinkRef = React.useRef(!!location.state?.activeReport);
  
  // Navigation state - initialize from location state or defaults
  const [activeCategory, setActiveCategory] = useState(
    location.state?.activeCategory || 'money'
  );
  const [activeReport, setActiveReport] = useState(() => {
    if (location.state?.activeReport) {
      return location.state.activeReport;
    }
    const category = REPORT_CATEGORIES[location.state?.activeCategory || 'money'];
    const firstReport = category?.reports.find(r => !r.comingSoon);
    return firstReport?.id || 'payments';
  });
  
  const [showReportSelector, setShowReportSelector] = useState(false);
  
  // Report state
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('year');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Filter state - includes both pre-business and personal transaction filtering
  const [excludePreBusiness, setExcludePreBusiness] = useState(true);
  const [excludePersonal, setExcludePersonal] = useState(true); // NEW: Personal/Free transaction filtering
  
  const [expandedSections, setExpandedSections] = useState({
    expenses: true,
    expenseLines: true,
    taxEstimates: true,
    customers: true,
    services: true,
    geography: false
  });
  
  // Report data
  const [profitLossData, setProfitLossData] = useState(null);
  const [scheduleCData, setScheduleCData] = useState(null);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null); const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [exporting, setExporting] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get current category and report info
  const currentCategory = REPORT_CATEGORIES[activeCategory];
  const currentReport = currentCategory?.reports.find(r => r.id === activeReport);
  const requiresPro = currentReport?.tier === 'Professional';

  // Calculate date range
  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (dateRange) {
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year':
        start = new Date(selectedYear, 0, 1);
        end = new Date(selectedYear, 11, 31);
        break;
      case 'all':
        start = new Date(2020, 0, 1);
        end = now;
        break;
      default:
        start = new Date(now.getFullYear(), 0, 1);
        end = now;
    }

    return { start, end };
  };

  // UPDATED: Include excludePersonal in dependency array
  useEffect(() => {
    loadReportData();
     }, [activeReport, dateRange, selectedYear, excludePreBusiness, excludePersonal, selectedQuarter]);

// When category changes, select first available report (but not on deep link)
useEffect(() => {
  // Skip if this was a deep link navigation
  if (isDeepLinkRef.current) {
    isDeepLinkRef.current = false; // Clear for next time
    return;
  }
  
  const category = REPORT_CATEGORIES[activeCategory];
  if (category) {
    const firstReport = category.reports.find(r => !r.comingSoon);
    
    // Only switch if we're NOT already on a valid report in this category
    const isCurrentReportInCategory = category.reports.some(r => r.id === activeReport);
    if (firstReport && !isCurrentReportInCategory) {
      setActiveReport(firstReport.id);
    }
  }
}, [activeCategory, activeReport]);

  const loadReportData = async () => {
    // Reports that load themselves
    const selfLoadingReports = ['scorecard', 'customers', 'payments', 'invoices', 'jobs'];
    if (selfLoadingReports.includes(activeReport)) {
      setLoading(false);
      return;
    }
    
    // Skip loading Pro-only reports if user doesn't have access
    if (requiresPro && !canAccessPro) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const { start, end } = getDateRange();

      if (activeReport === 'profitLoss') {
        // UPDATED: Include excludePersonal parameter
        const response = await reportsApi.getProfitLoss({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          excludePersonal: excludePersonal
        });
        setProfitLossData(response.data);
      } else if (activeReport === 'analytics') {
        // UPDATED: Include excludePersonal parameter for analytics
        const response = await reportsApi.getAnalytics({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          excludePersonal: excludePersonal
        });
        setAnalyticsData(response.data);
      } else if (activeReport === 'scheduleC') {
        // UPDATED: Include excludePersonal parameter
        const response = await reportsApi.getScheduleC({ 
          year: selectedYear,
          excludePreBusiness: excludePreBusiness,
          excludePersonal: excludePersonal
        });
        setScheduleCData(response.data);
      } else if (activeReport === 'quarterly') {
        // UPDATED: Include excludePersonal parameter
        const response = await reportsApi.getQuarterlyTax({ quarter: selectedQuarter ?? undefined, 
          year: selectedYear,
          excludePersonal: excludePersonal
        });
        setQuarterlyData(response.data);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate list of years for year selector
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const exportToPdf = async () => {
    if (exporting) return;
    
    try {
      setExporting(true);
      
      // This could be expanded to handle different report types
      const reportName = currentReport?.label || 'Report';
      
      // Trigger browser print dialog
      window.print();
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // Main category navigation
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Track your business performance</p>
        </div>
        <div className="flex items-center gap-2 mt-4 sm:mt-0">
          <button
            onClick={exportToPdf}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 print:hidden">
        {Object.values(REPORT_CATEGORIES).map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isActive
                  ? `bg-${category.color}-600 text-white`
                  : `bg-${category.color}-50 text-${category.color}-700 hover:bg-${category.color}-100`
              }`}
            >
              <Icon size={16} />
              {category.label}
            </button>
          );
        })}
      </div>

      {/* Report Selector */}
      <div className="mb-6 print:hidden">
        {/* Mobile Dropdown */}
        <div className="sm:hidden">
          <button
            onClick={() => setShowReportSelector(!showReportSelector)}
            className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
          >
            <div className="flex items-center gap-3">
              {currentReport?.icon && <currentReport.icon size={18} className="text-gray-500" />}
              <div className="text-left">
                <p className="font-medium text-gray-900">
                  {currentReport?.label || 'Select Report'}
                </p>
                <p className="text-xs text-gray-500">{currentReport?.description || ''}</p>
              </div>
            </div>
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${showReportSelector ? 'rotate-180' : ''}`} />
          </button>

          {showReportSelector && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
              {currentCategory?.reports.map((report) => {
                const isActive = activeReport === report.id;
                const needsPro = report.tier === 'Professional' && !canAccessPro;
                
                return (
                  <button
                    key={report.id}
                    onClick={() => {
                      if (!report.comingSoon) {
                        setActiveReport(report.id);
                        setShowReportSelector(false);
                      }
                    }}
                    disabled={report.comingSoon}
                    className={`w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors ${
                      isActive ? 'bg-primary-50' : ''
                    } ${report.comingSoon ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <report.icon size={16} className="text-gray-500" />
                      <div className="text-left">
                        <p className={`text-sm font-medium ${isActive ? 'text-primary-700' : 'text-gray-900'}`}>
                          {report.label}
                        </p>
                        <p className="text-xs text-gray-500">{report.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {needsPro && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">
                          PRO
                        </span>
                      )}
                      {report.comingSoon && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-bold rounded">
                          SOON
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Desktop Pills */}
        <div className="hidden sm:flex gap-2">
          {currentCategory?.reports.map((report) => {
            const isActive = activeReport === report.id;
            const needsPro = report.tier === 'Professional' && !canAccessPro;
            
            return (
              <button
                key={report.id}
                onClick={() => !report.comingSoon && setActiveReport(report.id)}
                disabled={report.comingSoon}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-white border-2 border-primary-600 text-primary-700 shadow-sm'
                    : report.comingSoon
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <report.icon size={14} />
                {report.label}
                {needsPro && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">
                    PRO
                  </span>
                )}
                {report.comingSoon && (
                  <span className="ml-1 px-1.5 py-0.5 bg-gray-200 text-gray-500 text-[10px] font-bold rounded">
                    SOON
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Date/Year Filters - Only show for reports that need them */}
      {(activeReport === 'profitLoss' || activeReport === 'analytics') && canAccessPro && (
        <div className="flex flex-wrap items-center gap-2 mb-4 print:hidden">
          <div className="relative">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          
          {dateRange === 'year' && (
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          )}
        </div>
      )}

      {(activeReport === 'scheduleC' || activeReport === 'quarterly') && (
        <div className="flex items-center gap-2 mb-4 print:hidden">
          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

      {/* Report Content */}
      {loading ? (
        <Loading />
      ) : (
        <div className="print:p-0">
          {/* Payment Summary - Solo+ */}
          {activeReport === 'payments' && (
            <PaymentSummary />
          )}

          {/* Invoice Aging - Solo+ */}
          {activeReport === 'invoices' && (
            <InvoiceAging />
          )}

          {/* Jobs Summary - Solo+ */}
          {activeReport === 'jobs' && (
            <JobsSummary />
          )}

          {/* Business Health Scorecard - Solo+ */}
          {activeReport === 'scorecard' && (
            <HealthScorecard />
          )}

          {/* Customer Intelligence - Pro+ */}
          {activeReport === 'customers' && (
            !canAccessPro ? (
              <UpgradePrompt 
                feature="pnl-reports" 
                title="Unlock Customer Intelligence"
                description="Identify at-risk customers, VIPs, win-back opportunities, and upsell candidates to grow your revenue."
              />
            ) : (
              <CustomerIntelligence />
            )
          )}

          {/* Profit & Loss - Pro+ */}
          {/* UPDATED: Pass excludePersonal props */}
          {activeReport === 'profitLoss' && (
            !canAccessPro ? (
              <UpgradePrompt 
                feature="pnl-reports" 
                title="Unlock Profit & Loss Reports"
                description="Get detailed P&L statements with period comparisons, expense breakdowns, and tax set-aside recommendations."
              />
            ) : (
              <ProfitLossReport 
                data={profitLossData}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                excludePersonal={excludePersonal}
                onExcludePersonalChange={setExcludePersonal}
              />
            )
          )}

          {/* Business Insights - Pro+ */}
          {activeReport === 'analytics' && (
            !canAccessPro ? (
              <UpgradePrompt 
                feature="pnl-reports" 
                title="Unlock Business Insights"
                description="Get detailed analytics including revenue trends, top customers, service performance, and collection insights."
              />
            ) : (
              <AnalyticsReport 
                data={analyticsData}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
              />
            )
          )}

          {/* Schedule C - Solo+ */}
          {/* UPDATED: Pass excludePersonal props */}
          {activeReport === 'scheduleC' && (
            <ScheduleCReport 
              data={scheduleCData}
              selectedYear={selectedYear}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              excludePreBusiness={excludePreBusiness}
              onExcludePreBusinessChange={setExcludePreBusiness}
              excludePersonal={excludePersonal}
              onExcludePersonalChange={setExcludePersonal}
            />
          )}

          {/* Quarterly Tax - Solo+ */}
          {activeReport === 'quarterly' && (
              <QuarterlyTaxReport data={quarterlyData} onQuarterChange={setSelectedQuarter} />
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;